import { type yup } from '@framework/validation/initYup.ts';
import { type AnyObject, type ObjectSchema } from 'yup';
import {
  type HydrateWidgetFn,
  type IHydratedWidget,
} from '../../hydrate/IHydrationContext.ts';
import { type IBooleanCondition } from '../../inputs/expressions-editor/conditions/IBooleanCondition.tsx';
import { type IReference } from '../expressions/references/IReference.ts';
import { type IReferencePath } from '../expressions/references/IReferencePath.ts';
import { type IFixedRuleType, type IRuleType } from '../rules/IRuleType.ts';
import { type SectionType } from '../sections/SectionTypeContext.tsx';
import { type TranslationWidgetSchema } from '../translations/schema/types/TranslationWidgetSchema.ts';
import { type IBaseWidget } from './IBaseWidget.ts';
import { type IInputWidget } from './repository/inputs/IInputWidget.ts';
import {
  type ISortableWidgetData,
  type Widget,
  type WidgetCategory,
  type WidgetMainType,
  type WidgetType,
} from './Widget.ts';
import { type IRuntimeWidgetOptionValue } from './WidgetOptionLogic.ts';

type HasValue<T extends IBaseWidget> = T extends IInputWidget ? boolean : false;
type WidgetRules<T extends IBaseWidget> = T extends IInputWidget
  ? IRuleType[]
  : [];

type WidgetFixedRules<T extends IBaseWidget> = T extends IInputWidget
  ? IFixedRuleType[]
  : [];

export type IParentWidgetScope = Array<{
  visible: IBooleanCondition | undefined;
  enabled: IBooleanCondition | undefined;
  /** Codice del widget. In questo modo posso sempre ottenere il path */
  code: string | undefined;
  /** Identifica se stiamo in una visualizzazione readonly */
  isReadonly?: boolean;
  /**
   * Valori del meta extra che possono essere passati
   * su tutto l'albero figlio
   */
  extraMeta?: Record<string, any>;
}>;

export type IParentWidgetScopeItem = IParentWidgetScope[0];

export interface ICreateWidgetFormSchemaInput<W extends IBaseWidget> {
  widget: W;
  /**
   * Permette a un parent di passare le sue regole di visibilità così
   * da applicarle a tutto l'albero figlio
   */
  parentScope: IParentWidgetScope;
  section: number;
  page: number;
}

/**
 * Interfaccia che descrive l'oggetto "type" passato al repository per la gestione
 * centralizzata dei componenti.
 */
export interface IWidgetType<W extends IBaseWidget> {
  type: WidgetType;
  mainType: WidgetMainType;
  category: WidgetCategory;
  /** Nome del Widget mostrato in fase di configurazione  */
  name: string;
  icon?: React.ReactNode;
  /**
   * Specifica per quali tipi di sezione è disponibile questo widget.
   * Se non è specificato, il widget è disponibile per tutti i tipi di sezione.
   * Se è specificato un array vuoto, il widget non è disponibile in nessuna sezione.
   */
  supportedSectionTypes?: SectionType[];
  /** Specifica se il Widget è grafico (false) o gestisce un valore (true) */
  hasValue: HasValue<W>;
  /**
   * Specifica il tipo di riferimento che il widget può avere. Un widget
   * grafico non genera riferimenti, mentre un widget di input genera
   * un riferimento al valore inserito dall'utente.
   * Widget più complessi possono generare più riferimenti, ad esempio un
   * widget di input lista
   */
  getReference?: (path: IReferencePath, widget: W) => IReference | null;
  /**
   * Permette di ritornare, oltre al riferimento principale, anche altri
   * riferimenti che possono essere calcolati a partire dal widget.
   */
  getSiblingReferences?: (path: IReferencePath, widget: W) => IReference[];
  /**
   * Permette di generare riferimenti per i figli di un widget. Ad esempio
   * la tabella di input materializza la matrice righe per colonne, quindi
   * da una singola tabella si generano più riferimenti N x M.
   */
  materializeChildren?: (widget: W, prefix?: string) => Widget[];
  /**
   * Dato un nodo che è figlio di questo Widget, quali sono le altre reference
   * che non possono essere calcolate dai `children`, ovvero quelle virtuali?
   * È il caso delle tabelle, che hanno un riferimento per ogni colonna
   */
  getParentVirtualReferences?: (
    path: IReferencePath,
    widget: W,
  ) => IReference[];

  /** Ordine di visualizzazione del widget all'interno del container */
  childrenLayoutStrategy?: 'vertical' | 'horizontal';

  /** Componente input lato utente */
  formComponent: React.ComponentType<IWidgetComponentProps<W>>;
  optionComponent?: React.ComponentType<IWidgetComponentOptionProps<W>>;

  /**
   * In caso di rendering molto diverso fra riepilogo e compilazione,
   * è possibile utilizzare questo componente per il riepilogo.
   * Di default viene utilizzato il `formComponent`, a cui viene passato
   * il parametro `readonly: true`
   */
  readonlyComponent?: React.ComponentType<IWidgetComponentProps<W>>;

  /**
   * Idratazione dei widget da schema generale a schema corrispondente ai valori
   * forniti. Una sorta di rendering ma in JSON
   */
  hydrate?: HydrateWidgetFn<W>;

  /** Validatore input */
  createFormSchema?: (
    input: ICreateWidgetFormSchemaInput<W>,
  ) => Record<string, yup.AnySchema>;

  /** Form di configurazione widget  */
  configComponent: React.ComponentType<IWidgetComponentProps<W>> | null;
  configOptions?: IWidgetConfigOptions;
  /** Validatore configurazione componente */
  configValidator: ObjectSchema<AnyObject> | null;

  /** Componente "dumb" mostrato nel D'n'D all'utente configuratore */
  previewComponent: React.ComponentType<IWidgetComponentProps<W>>;

  /** Regole attivabili sul componente */
  rules: WidgetRules<W>;
  /** Regole statiche di validazione sul widget, che tuttavia devono essere applicate solo all'inoltro */
  fixedRules?: WidgetFixedRules<W>;

  /**
   * Rendering HTML puro per i template documento (PDF).
   * Solo i widget che implementano questa funzione sono disponibili
   * nell'editor-light dei documenti.
   * Ritorna stringa vuota per widget che partecipano all'albero
   * senza produrre output visibile (es. computed).
   */
  renderHtml?: (
    widget: IHydratedWidget<W>,
    context: IHtmlRenderContext,
  ) => string | null;

  /** Gestione condizioni D'n'D */
  /** Ritorna vero se è possibile droppare il `widget` su `parent` */
  isValidParent?: (
    parent: IWidgetType<Widget> | null,
    parentData?: ISortableWidgetData,
  ) => boolean;
  /** Ritorna vero se è possibile droppare il widget `child` su `widget` */
  isValidChild?: (widget: IWidgetType<Widget> | null) => boolean;
  /** Schema per ottenere e sostituire le chiavi di traduzione */
  translationSchema: TranslationWidgetSchema;
  /** Permette di specificare una larghezza differente per il drawer (e.g. per le sottosezioni)*/
  drawerWidth?: number | string;
  /** Se TRUE nasconde le configurazioni relative alle regole di visualizzazione (enable/visible) in fase di configurazione */
  hideFlags?: boolean;
  /** Se TRUE il campo non può essere disabilitato. Rimuove la configurazione da UI e salta la modifica dello schema in setEditableRule() */
  alwaysEnabled?: boolean;
  /**
   * Se TRUE, il widget ha un tipo configurabile (e.g. switch/hidden). Con questa informazione possiamo gestire con più facilità
   * le casistiche di configurazione (e.g. metadati).
   */
  dynamicReference?: boolean;
  /**
   * Alias del widget, utilizzato per permettere una ricerca più semplice in fase di configurazione
   * (e.g. Pulsante = Bottone, Async = Servizio Esterno, ...)
   */
  aliases?: string[];
  /**
   * Permette di recuperare in modo centralizzato tutte le chiavi dei siblings
   * di un widget. Per esempio, alcuni widget potrebbero generare valori secondari oltre
   * a quello principale (e.g. idDocumento + nome del documento, il secondo salvato con una chiave composta).
   *
   * Con questa funzione torniamo la lista di tutte le chiavi che il widget può generare.
   * Utilizzata per esempio per il reset dei valori in caso di azioni particolari (ModuleActionService).
   */
  getSiblingKeys?: (code: string) => string[];
}

export function getWidgetTypeAliases(widget: IWidgetType<any>): string[] {
  return [widget.name ?? widget.type, ...(widget.aliases ?? [])];
}

export function isWidgetTypeAlias(
  widget: IWidgetType<any>,
  search: string,
): boolean {
  return getWidgetTypeAliases(widget).some(a =>
    a.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
}

export interface IWidgetComponentProps<W extends IBaseWidget> {
  widget: W;
  /** Permette di gestire alcune proprietà del widget, come nascondere campi specifici (placeholder, help, label, ...) */
  componentLayout?: IWidgetComponentPropsConfig;
  hideConfigPlaceholder?: boolean;
  children?: React.ReactNode;
  /** Se si tratta di una visualizzazione in preview */
  preview?: boolean;
  /** nome del componente, obbligatorio solo in modalità compilazione */
  name?: string;
  readonly?: boolean;
}

/**
 * Configurazione delle props del componente widget. In questo modo attraverso le props è possibile gestire le proprietà grafiche del widget,
 * nascondendo alcuni elementi, in maniera centralizzata e uguale per tutti i widget.
 */
export interface IWidgetComponentPropsConfig {
  hidePlaceholder?: boolean;
  hideLabel?: boolean;
  hideHelp?: boolean;
  /** Per i campi che possiedono un text, come i widget booleani */
  hideText?: boolean;
  /** Impostato a true se il widget si trova dentro la colonna di una tabella */
  isTableColumn?: boolean;
}

export interface IWidgetComponentOptionProps<W extends IBaseWidget> {
  widget: W;
  name: string;
  option: IRuntimeWidgetOptionValue | null | undefined;
  groupName: string;
  hideLabel?: boolean;
  /** Se si tratta di una visualizzazione in preview */
  preview?: boolean;
  readonly?: boolean;
}

export interface IWidgetConfigOptions {
  /** Nasconde le direttive */
  hideDirectives?: boolean;
}

/**
 * Contesto passato alla funzione `renderHtml` di un widget.
 */
export interface IHtmlRenderContext {
  /** Renderizza ricorsivamente i widget figli in HTML */
  renderChildren: (
    widgets: IHydratedWidget<any>[],
    transform?: (widget: IHydratedWidget<any>, html: string) => string,
  ) => string;
  /** Se true, maschera i valori sensibili */
  omissis: boolean;
  /** Se true, rendering intermedio (bozza) */
  intermedio: boolean;
  /** Mappa delle immagini disponibili per il rendering (chiave: imageId) */
  images?: Record<string, { base64: string; contentType?: string | null }>;
  /** Contenuti HTML delle sezioni operatore, mappa placeholder -> HTML. Iniettati da renderSectionHtml nel div data-placeholder-section-id. */
  sections?: Record<string, string>;
}
